"use client";

/**
 * Standalone bottom input bar that REPLACES `<ReplayControls>` when the
 * demo-input feature flag is on (see `lib/demo-replay-config.ts`).
 *
 * Why this exists: when the founder screen-records a replay session
 * for an influencer, the bottom of the page should look like the real
 * Xynth chat input — not like a video player. Visitors should be able
 * to *type* in the demo, and on submit either:
 *   1. Get routed to a different curated replay if the prompt contains
 *      a configured keyword (`KEYWORD_REPLAY_ROUTES`), or
 *   2. Get sent to the home page with `?prefill=<text>` so the visitor
 *      can convert into a real Xynth chat.
 *
 * We re-use `<MultimodalInput variant="workflow">` so the visual /
 * interactive surface is exactly the same as the real input bar
 * (auto-resize textarea, attach, audio, send button, prompt typewriter
 * placeholder). The `workflow` variant exists specifically for this
 * "input shell with custom submit" use-case — its `onStandaloneSubmit`
 * callback fires instead of useChat's `handleSubmit`, which means we
 * can intercept everything and route however we want.
 *
 * Visitor sign-in note: we do NOT gate routing on auth. The whole
 * point of the demo bar is to feel native; gating with an AuthModal
 * would break the keyword router (which is supposed to flip the
 * replay seamlessly with no friction). Routing to `/?prefill=` lands
 * on the public landing page where the regular auth gate fires when
 * the user actually presses send on the real chat.
 */

import { useRouter } from "next/navigation";
import { useState, useCallback, useEffect, useRef } from "react";
import type { Attachment } from "ai";
import { MultimodalInput } from "@/components/multimodal-input";
import {
  findKeywordReplayRoute,
  useKeywordReplayRoutingEnabled,
} from "@/lib/demo-replay-config";
import { createChannelLogger } from "@/lib/debug-log";

const demoLog = createChannelLogger("replay_debug");

interface DemoReplayInputProps {
  /** Replay session ID (used for telemetry / logging only — no chat is started). */
  sessionId: string;
  /** Curated replay slug currently playing. Logged for telemetry. */
  curatedId?: string;
  /**
   * The replay's canned next-user-message for the following turn, or
   * null when no gate is pending (mid-stream, or after the final turn).
   * When truthy, the input bar becomes a "click to continue" gate:
   * the textarea is prefilled + locked read-only and the Send button
   * lights up. Wired from chat.tsx's `nextUserMessage` useMemo, which
   * derives the value synchronously from the SSE `data` array.
   */
  nextUserMessage?: string | null;
  /**
   * Called with the canned next-user-message when the user clicks Send
   * while a gate is pending. chat.tsx uses this to consume the gate
   * (advancing `nextUserMessageConsumedAt`) and `append()` the message
   * as a user message, which triggers the next SSE turn. Must be the
   * same callback shape as `<ReplayControls>.onContinue`.
   */
  onContinueReplay?: (message: string) => void;
  /**
   * Clean replay mode — skips the auto-prefill of `nextUserMessage`
   * into the textarea, leaves the input editable, and forwards
   * whatever the user typed to `onContinueReplay` (the backend
   * ignores the message content — only the act of submitting
   * advances the recorded `current_turn`). Used by the /replay/[id]/clean
   * route. Defaults to false so the standard auto-prefill / read-only
   * gate UX is unchanged.
   */
  disableAutoPrefill?: boolean;
  /**
   * Live `useChat` status forwarded from the parent. Used ONLY in
   * clean mode: an advance is allowed whenever the stream is
   * `"ready"` (finished), without requiring `nextUserMessage` to
   * have arrived. This sidesteps brittle dependencies on the
   * backend's `next_user_message` SSE frame for turns where the
   * frame may not fire or has already been consumed.
   */
  chatStatus?: "submitted" | "streaming" | "ready" | "error";
}

export function DemoReplayInput({
  sessionId,
  curatedId,
  nextUserMessage,
  onContinueReplay,
  disableAutoPrefill = false,
  chatStatus,
}: DemoReplayInputProps) {
  const router = useRouter();
  const keywordRoutingOn = useKeywordReplayRoutingEnabled();

  // Render-level diagnostic so the log shows when the gate flips. Cheap;
  // the log helper is a no-op in production and on non-localhost.
  demoLog("demo-input:render", {
    sessionId,
    curatedId,
    hasNextUserMessage: Boolean(nextUserMessage),
    nextLen: nextUserMessage?.length ?? 0,
    nextPreview: (nextUserMessage ?? "").slice(0, 80),
    hasOnContinueReplay: Boolean(onContinueReplay),
  });

  // MultimodalInput's `variant="workflow"` calls our submit handler with
  // (text, attachments). It does NOT thread these into a chat session
  // — the input is purely cosmetic from useChat's perspective. We
  // manage the textarea state ourselves via the input/setInput pair,
  // and attachments are local-only (no upload pipeline).
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<Array<Attachment>>([]);

  // Replay-gate: when a new `nextUserMessage` arrives, dispatch the
  // existing `xynth:prefill-input` window event so MultimodalInput's
  // listener drops the canned text into its internal `draft` state,
  // focuses the textarea, and places the cursor at the end. This
  // re-uses the battle-tested prefill bus (see multimodal-input.tsx
  // "Cross-component prefill listener") instead of plumbing a new
  // prop through — MultimodalInput's textarea is bound to its own
  // internal draft state, not to our input/setInput above, so prop
  // plumbing would silently no-op.
  //
  // We track the *value* of the last-prefilled message in a ref so
  // re-renders don't re-fire the prefill on every render. A NEW
  // nextUserMessage value (next turn) fires exactly once.
  const lastPrefilledRef = useRef<string | null>(null);
  useEffect(() => {
    // Clean replay mode: never auto-prefill. The viewer types their
    // own message; submit still advances the recorded turn via
    // `onContinueReplay` (see handleStandaloneSubmit below).
    if (disableAutoPrefill) return;
    if (!nextUserMessage) {
      lastPrefilledRef.current = null;
      return;
    }
    if (lastPrefilledRef.current === nextUserMessage) return;
    lastPrefilledRef.current = nextUserMessage;
    demoLog("demo-input:prefill-from-gate", {
      session: sessionId,
      curated: curatedId,
      text_preview: nextUserMessage.slice(0, 80),
    });
    // Defer one tick so MultimodalInput is mounted + its listener
    // attached before we dispatch (matters on the very first render
    // of a replay where the gate may arrive racing with mount).
    const t = window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("xynth:prefill-input", {
          detail: { text: nextUserMessage },
        }),
      );
    }, 0);
    return () => window.clearTimeout(t);
  }, [nextUserMessage, sessionId, curatedId, disableAutoPrefill]);

  const handleStandaloneSubmit = useCallback(
    (text: string, _atts: Array<Attachment>) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      // ── Clean replay mode advance ───────────────────────────────
      // In clean mode, ANY submit advances the recorded replay. We
      // don't gate on `nextUserMessage` (the backend's SSE frame is
      // unreliable across turns) and we don't gate on `chatStatus`
      // (`useChat.append()` cleanly aborts an in-flight request and
      // POSTs a new one). The backend continuation is keyed on
      // `state.current_turn`, so any user message just streams the
      // next recorded turn. The user's typed text becomes the user
      // bubble in the transcript; the assistant turn is the canned
      // recording.
      if (disableAutoPrefill && onContinueReplay) {
        demoLog("demo-input:continue-replay", {
          from_session: sessionId,
          from_curated: curatedId,
          mode: "clean",
          text_preview: trimmed.slice(0, 80),
          chat_status: chatStatus,
          had_gate: Boolean(nextUserMessage),
        });
        // Clear the textarea immediately so the viewer sees the
        // submission "land" right away.
        setInput("");
        window.dispatchEvent(
          new CustomEvent("xynth:prefill-input", { detail: { text: "" } }),
        );
        // Delay the actual stream-advance by ~1s so the user-bubble
        // appearance and the assistant's recorded reply don't snap in
        // back-to-back. Looks more like a natural conversational beat
        // — without it the clean replay reads as too eager / robotic.
        const captured = trimmed;
        window.setTimeout(() => {
          onContinueReplay(captured);
        }, 1000);
        return;
      }

      // Replay-gate continuation wins over everything else. When the
      // gate is open, the user physically can't have typed anything
      // else (textarea is read-only), so `trimmed` should equal the
      // canned `nextUserMessage`. We pass the canned value straight
      // through to onContinueReplay — this is what chat.tsx turns
      // into an `append({ role: "user", content: msg })` that fires
      // the next SSE turn.
      if (nextUserMessage && onContinueReplay) {
        const continuationText = nextUserMessage;
        demoLog("demo-input:continue-replay", {
          from_session: sessionId,
          from_curated: curatedId,
          mode: "canned",
          text_preview: continuationText.slice(0, 80),
        });
        onContinueReplay(continuationText);
        // Explicit clear so the textarea visibly empties on Send —
        // exactly as it would for a real chat submission. Both our
        // local mirror state AND MultimodalInput's internal `draft`
        // state need to be reset; they're decoupled (see comment on
        // the prefill effect above for the why), so we hit both:
        //   1. setInput("") clears our mirror.
        //   2. xynth:prefill-input dispatch with empty text clears
        //      MultimodalInput's draft via the same bus we used to
        //      prefill it. Without this, MultimodalInput's submitForm
        //      DOES call setDraft("") in workflow mode, but a stale
        //      `nextUserMessage` value during a same-tick re-render
        //      can re-fire the prefill listener and the textarea
        //      visually retains the prompt for an extra frame —
        //      reads to the viewer as "clicking Send did nothing".
        setInput("");
        window.dispatchEvent(
          new CustomEvent("xynth:prefill-input", { detail: { text: "" } }),
        );
        return;
      }

      // Keyword router (feature #4). Fires only if the matching flag
      // is also on; otherwise we fall straight through to /?prefill=.
      // Only reachable when no gate is pending (before the first turn
      // or after the final turn) because the gate-continue branch
      // above short-circuits first.
      if (keywordRoutingOn) {
        const route = findKeywordReplayRoute(trimmed);
        if (route) {
          demoLog("demo-input:keyword-route", {
            from_session: sessionId,
            from_curated: curatedId,
            keyword: route.keyword,
            to_curated: route.curatedId,
            text_preview: trimmed.slice(0, 80),
          });
          // Use replace so the browser back-button doesn't trap the
          // viewer in a chain of replay→replay→replay history entries
          // — they should be able to back out to wherever they came
          // from before the first replay landed.
          router.replace(`/replay/${route.curatedId}`);
          return;
        }
      }

      // Default: prefill the home chat with the typed text. Same
      // mechanism the post-replay input uses on the "Replay Complete"
      // state, so visitors get a consistent path back into a real chat.
      demoLog("demo-input:prefill-fallback", {
        from_session: sessionId,
        from_curated: curatedId,
        text_preview: trimmed.slice(0, 80),
      });
      router.push(`/?prefill=${encodeURIComponent(trimmed)}`);
    },
    [
      keywordRoutingOn,
      router,
      sessionId,
      curatedId,
      nextUserMessage,
      onContinueReplay,
      disableAutoPrefill,
      chatStatus,
    ],
  );

  return (
    <form
      className="flex mx-auto px-0 bg-background pb-4 md:pb-6 gap-2 w-full md:max-w-3xl"
      data-demo-replay-input="true"
    >
      <MultimodalInput
        chatId={sessionId}
        input={input}
        setInput={setInput}
        // No-op: workflow variant routes through onStandaloneSubmit
        // instead. Required by the prop type, never invoked.
        handleSubmit={undefined}
        // Streaming state irrelevant — there's no real chat under us.
        status="ready"
        stop={undefined}
        attachments={attachments}
        setAttachments={setAttachments}
        // No upload pipeline in demo mode; let the user attach files
        // visually (so the demo feels real) but don't try to delete
        // them from a backend on remove.
        onRemoveAttachment={(a) => {
          setAttachments((prev) => prev.filter((x) => x.url !== a.url));
        }}
        messages={[]}
        variant="workflow"
        onStandaloneSubmit={handleStandaloneSubmit}
        // Suppress the rotating prompt typewriter ("give me detailed
        // breakdown of nvda earnings…" etc.) during a recorded replay.
        // An empty single-element array short-circuits the typewriter
        // in MultimodalInput and leaves the placeholder fully blank,
        // so the viewer's eye stays on the replay content with no
        // input-bar text to read.
        placeholderTexts={[""]}
        // Hide the tools selector & deep research toggle in demo mode
        // — they look real but, since we never submit, every option
        // would be a dead-end. Keeping the bar focused on *typing*
        // matches what a clean recording calls for.
        hideData
        // Replay-gate handoff: when the SSE stream has emitted a
        // `next_user_message` for the following turn, lock the
        // textarea (user can't edit the canned message) and light
        // up the Send button (clear visual cue that clicking it is
        // the action to continue the demo). Both flip back to off
        // automatically once `nextUserMessage` clears — either when
        // the user clicks Send (chat.tsx advances the consumed
        // cursor) or when a new turn starts streaming.
        // Clean mode keeps the textarea editable AND skips the Send-button
        // highlight so the bar reads as a normal composer the viewer is
        // expected to type into. The implicit "press enter to advance" UX
        // is communicated by the recording itself, not by a glow.
        forceReadOnly={!disableAutoPrefill && Boolean(nextUserMessage)}
        highlightSend={!disableAutoPrefill && Boolean(nextUserMessage)}
      />
    </form>
  );
}
